// Small shared building blocks matching the artboard tokens (docs/design/*.dc.html):
// white card, 12px radius, #e5e7eb border, soft shadow, brand-red pills.
import type { ReactNode } from "react";

export function Card({ children, className = "" }: { children: ReactNode; className?: string }) {
  return (
    <div className={`rounded-card border border-neutral-200 bg-white p-5 shadow-sm ${className}`}>{children}</div>
  );
}

export function Label({ children }: { children: ReactNode }) {
  return <div className="text-[11px] font-bold uppercase tracking-wider text-neutral-400">{children}</div>;
}

export function Sub({ children }: { children: ReactNode }) {
  return <span className="text-xs text-neutral-400">{children}</span>;
}

const PILL_TONES: Record<string, string> = {
  ok: "bg-green-50 text-green-600 border-green-200",
  amber: "bg-amber-50 text-amber-600 border-amber-200",
  red: "bg-red-50 text-brand border-red-200",
  grey: "bg-neutral-50 text-neutral-500 border-neutral-200",
};

export function Pill({ tone, children }: { tone: keyof typeof PILL_TONES; children: ReactNode }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${PILL_TONES[tone]}`}
    >
      {children}
    </span>
  );
}

const DOT_TONES: Record<string, string> = {
  ok: "bg-green-600",
  amber: "bg-amber-600",
  red: "bg-brand",
  grey: "bg-neutral-300",
};

export function Dot({ tone = "ok" }: { tone?: keyof typeof DOT_TONES }) {
  return <span className={`inline-block h-2 w-2 shrink-0 rounded-full ${DOT_TONES[tone]}`} />;
}

export function Btn({
  children,
  onClick,
  secondary,
  disabled,
  type = "button",
}: {
  children: ReactNode;
  onClick?: () => void;
  secondary?: boolean;
  disabled?: boolean;
  type?: "button" | "submit";
}) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={
        "inline-flex min-h-[44px] items-center justify-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold disabled:opacity-50 " +
        (secondary
          ? "border border-neutral-200 bg-neutral-50 text-neutral-600 hover:bg-neutral-100"
          : "border border-brand bg-brand text-white hover:bg-brand-dark")
      }
    >
      {children}
    </button>
  );
}

export function Th({ children, right }: { children: ReactNode; right?: boolean }) {
  return (
    <th
      className={`border-b border-neutral-200 pb-2.5 text-[11px] font-bold uppercase tracking-wider text-neutral-400 ${right ? "text-right" : "text-left"}`}
    >
      {children}
    </th>
  );
}

export function Td({ children, right, className = "" }: { children: ReactNode; right?: boolean; className?: string }) {
  return (
    <td
      className={`border-b border-neutral-100 py-2.5 align-middle text-ink ${right ? "text-right tabular-nums" : ""} ${className}`}
    >
      {children}
    </td>
  );
}

export function Chip({ on, children, onClick }: { on?: boolean; children: ReactNode; onClick?: () => void }) {
  return (
    <button
      onClick={onClick}
      className={
        "rounded-full border px-3 py-1 text-xs font-medium " +
        (on ? "border-brand bg-red-50 text-brand" : "border-neutral-200 bg-neutral-50 text-neutral-500 hover:bg-neutral-100")
      }
    >
      {children}
    </button>
  );
}

export function relativeTime(iso: string | null | undefined): string {
  if (!iso) return "never";
  const then = new Date(iso.includes("T") || iso.endsWith("Z") ? iso : `${iso.replace(" ", "T")}Z`).getTime();
  if (Number.isNaN(then)) return iso;
  const diffMs = Date.now() - then;
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
}
